import React, { useId } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

// Select variants
export const selectVariants = {
  base: 'flex h-10 w-full rounded-md border border-border bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  navigation:
    'inline-flex h-11 min-h-11 min-w-0 max-w-full appearance-none rounded-md border border-transparent bg-transparent py-2 pl-2 pr-7 text-base font-medium text-foreground ring-offset-background hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
} as const;

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  variant?: 'default' | 'navigation';
  label?: string;
  error?: string;
  helperText?: string;
  children: React.ReactNode;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, variant = 'default', label, error, helperText, id, name, children, ...props },
    ref,
  ) => {
    // Use provided id, name-based id, or generate a stable id
    const generatedId = useId();
    const selectId = id || (name ? `select-${name}` : generatedId);

    const navigation = variant === 'navigation';
    const descriptionId = error ? `${selectId}-error` : helperText ? `${selectId}-help` : undefined;
    const input = (
      <select
        id={selectId}
        name={name}
        className={cn(
          navigation ? selectVariants.navigation : selectVariants.base,
          error && (navigation ? 'border-danger' : 'border-red-500'),
          className,
        )}
        ref={ref}
        aria-invalid={navigation && error ? true : undefined}
        aria-describedby={navigation ? descriptionId : undefined}
        {...props}
      >
        {children}
      </select>
    );
    return (
      <div
        className={navigation ? 'inline-flex max-w-full min-w-0 flex-col align-top' : 'space-y-1'}
      >
        {label && (
          <label
            htmlFor={selectId}
            className={navigation ? 'sr-only' : 'text-foreground block text-sm font-medium'}
          >
            {label}
          </label>
        )}
        {navigation ? (
          <div className="relative inline-flex max-w-full min-w-0">
            {input}
            <ChevronDownIcon
              aria-hidden="true"
              className="text-muted pointer-events-none absolute top-1/2 right-2 size-3.5 -translate-y-1/2"
            />
          </div>
        ) : (
          input
        )}
        {error && (
          <p
            id={navigation ? descriptionId : undefined}
            className={navigation ? 'text-danger text-sm' : 'text-sm text-red-600'}
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={navigation ? descriptionId : undefined} className="text-muted text-sm">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);
Select.displayName = 'Select';
